import { Box, CircularProgress, Stack, styled, Typography } from '@mui/material';
import { MARKER_RADIUS, STATUS_COLOR_MAP } from 'const/floorPlan';
import { FloorPlanStage } from '../../elements/FloorPlanStage';
import { ViewerToolbar } from '../../elements/ViewerToolbar';
import { useInjection } from './hook';
import { DialogConfirm } from 'app/components/elements/DialogConfirm';
import { IssueModal } from '../../elements/IssueModal';
import { DeficienciesSidebar } from '../../elements/ViewerSidebar';

type FloorPlanIssueViewerProps = {
  floorPlanUrl: string;
  floorPlanId?: number;
};

export const FloorPlanIssueViewer = ({ floorPlanUrl, floorPlanId }: FloorPlanIssueViewerProps) => {
  const {
    zoom,
    pan,
    scale,
    base,
    issueModal,
    isDropTarget,
    isDeleteConfirmOpen,
    canPan,
    canvasRef,
    dragGhostRef,
    onReady,
    setNaturalSize,
    zoomIn,
    zoomOut,
    fitToScreen,
    handleStageMouseLeave,
    cancelMarkerDelete,
    confirmMarkerDelete,
    handleIssueTextChange,
    closeIssueModal,
    handleIssueSubmit,
    handleToolDragStart,
    handleToolDragEnd,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    isLoading,
    setisLoading,
    issues,
    total
  } = useInjection(floorPlanId);

  return (
    <WrapperViewer>
      <ViewerContent>

      <ViewerToolbar
        zoom={zoom}
        onFitToScreen={fitToScreen}
        onZoomIn={zoomIn}
        onZoomOut={zoomOut}
        onToolDragStart={handleToolDragStart}
        onToolDragEnd={handleToolDragEnd}
        total={total}
        />

      <ViewerBody sx={{ position: 'relative' }}>
        <FloorPlanStage
          containerRef={canvasRef}
          imageUrl={floorPlanUrl}
          base={base}
          pan={pan}
          scale={scale}
          canPan={canPan}
          onCanvasReady={onReady}
          isDropTarget={isDropTarget}
          onNaturalSize={setNaturalSize}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onMarkerLayerMouseLeave={handleStageMouseLeave}
          onImageLoad={() => {
            setisLoading(false);
          }}
          />
        {isLoading && (
          <EmptyState>
            <CircularProgress
              size={36}
              sx={{
                color: '#226AAA'
              }}
              />

            <Typography color="#6B7280">Loading floor plan...</Typography>
          </EmptyState>
        )}
      </ViewerBody>

      {/* Off-screen drag image: a copy of the marker circle, so dragging the
          sidebar tool shows the marker rather than the sidebar row. */}
      <DragGhost ref={dragGhostRef} aria-hidden />
        {issueModal?.visible && (
          <IssueModal
          state={issueModal}
          onChangeText={handleIssueTextChange}
          onClose={closeIssueModal}
          onSubmit={handleIssueSubmit}
          />
        )}

      <DialogConfirm
        isOpen={isDeleteConfirmOpen}
        onClose={cancelMarkerDelete}
        onSubmit={confirmMarkerDelete}
        textTitle="Delete Issue"
        textContent="Are you sure you want to delete this issue? This action cannot be undone."
        textButton="Delete"
        textCancel="Cancel"
        isDelete
        />
    </ViewerContent>
     <SidebarWrapper>
      <DeficienciesSidebar issues={issues} total={total}/>
      </SidebarWrapper>
    </WrapperViewer>
  );
};

const WrapperViewer = styled(Stack)(() => ({
  width: '100%',
  height: '100%',
  minHeight: 0,
  display: 'flex',
  flexDirection: 'row',
  overflow: 'hidden'
}));

const ViewerBody = styled(Stack)(() => ({
  flexDirection: 'row',
  flex: 1,
  minHeight: 0,
  overflow: 'hidden'
}));

const DragGhost = styled(Box)(() => ({
  position: 'fixed',
  top: 0,
  left: '-384px',
  width: MARKER_RADIUS * 2,
  height: MARKER_RADIUS * 2,
  borderRadius: '50%',
  border: '2px solid #FFFFFF',
  backgroundColor: STATUS_COLOR_MAP.OPEN,
  pointerEvents: 'none'
}));

const EmptyState = styled(Stack)(() => ({
  width: '100%',
  height: '100%',
  minHeight: '350px',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '16px'
}));

const ViewerContent = styled(Stack)(() => ({
  flex: '0 0 60%',
  width: '60%',
  height: '100%',
  minWidth: 0,
  minHeight: 0,
  overflow: 'hidden',
  backgroundColor: '#F7F8FA',
  border: '2px solid #E4E7EC',
  borderRadius: '8px',
}));

const SidebarWrapper = styled(Stack)(() => ({
  flex: '0 0 39%',
  width: '39%',
  height: '100%',
  minWidth: 0,
  minHeight: 0,
  overflow: 'hidden',
  backgroundColor: '#FFFFFF',
  marginLeft:"10px",
  border: '2px solid #E4E7EC',
  borderRadius: '8px',
}));